import { type HHMM } from '@/app/services/solution/types/common';
import {
  type Timeline,
  type TimelineSegment,
  type TimelineSegmentType,
} from '@/app/services/solution/types/get-vehicle-overview';
import { type RouteWeekDay } from '@/app/services/solution/types/get-vehicle-route';

const toMinutes = (t: HHMM): number => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

const isDepotWindow = (startMin: number, endMin: number): boolean => {
  const mid = Math.floor((startMin + endMin) / 2);
  return mid < 360 || mid >= 1200;
};

const computeTypeMinutes = (segments: TimelineSegment[]): Record<TimelineSegmentType, number> => {
  const sorted = segments.toSorted((a, b) => toMinutes(a.time) - toMinutes(b.time));

  const totals: Record<TimelineSegmentType, number> = {
    driving: 0,
    charging: 0,
    idling: 0,
    out_of_charge: 0,
  };

  for (let index = 0; index < sorted.length; index++) {
    const current = sorted[index];
    const next = sorted.at(index + 1);
    const start = toMinutes(current.time);
    const end = next ? toMinutes(next.time) : 1440;
    const dur = Math.max(0, end - start);
    totals[current.type] += dur;
  }

  return totals;
};

const computeChargingBreakdown = (segments: TimelineSegment[]): { depot: number; topUp: number } => {
  const sorted = segments.toSorted((a, b) => toMinutes(a.time) - toMinutes(b.time));

  let depot = 0;
  let topUp = 0;

  for (let index = 0; index < sorted.length; index++) {
    const current = sorted[index];
    const next = sorted.at(index + 1);
    const start = toMinutes(current.time);
    const end = next ? toMinutes(next.time) : 1440;
    const dur = Math.max(0, end - start);

    if (current.type !== 'charging' || dur === 0) continue;

    if (isDepotWindow(start, end)) depot += dur;
    else topUp += dur;
  }

  return { depot, topUp };
};

const buildTimeline = (segments: TimelineSegment[]): Timeline => {
  const totals = computeTypeMinutes(segments);
  const breakdown = computeChargingBreakdown(segments);

  return {
    segments,
    legend: [
      { type: 'driving', totalMinutes: totals.driving },
      {
        type: 'charging',
        totalMinutes: totals.charging,
        breakdown:
          totals.charging > 0
            ? [
                { type: 'depot', minutes: breakdown.depot },
                { type: 'top_up', minutes: breakdown.topUp },
              ]
            : undefined,
      },
      { type: 'idling', totalMinutes: totals.idling },
      { type: 'out_of_charge', totalMinutes: totals.out_of_charge },
    ],
  };
};

export const vehicleOverviewTimelineByDay: Record<RouteWeekDay, Timeline> = {
  monday: buildTimeline([
    { time: '00:00', type: 'charging' },
    { time: '05:40', type: 'idling' },
    { time: '06:10', type: 'driving' },
    { time: '09:05', type: 'idling' },
    { time: '09:35', type: 'driving' },
    { time: '12:10', type: 'idling' },
    { time: '12:40', type: 'driving' },
    { time: '16:50', type: 'idling' },
    { time: '17:20', type: 'charging' },
  ]),
  tuesday: buildTimeline([
    { time: '00:00', type: 'charging' },
    { time: '06:00', type: 'driving' },
    { time: '08:30', type: 'idling' },
    { time: '09:00', type: 'driving' },
    { time: '11:10', type: 'charging' },
    { time: '11:45', type: 'driving' },
    { time: '15:40', type: 'idling' },
    { time: '16:15', type: 'driving' },
    { time: '18:00', type: 'charging' },
  ]),
  wednesday: buildTimeline([
    { time: '00:00', type: 'charging' },
    { time: '05:30', type: 'idling' },
    { time: '06:00', type: 'driving' },
    { time: '10:10', type: 'idling' },
    { time: '10:55', type: 'driving' },
    { time: '13:30', type: 'charging' },
    { time: '14:05', type: 'driving' },
    { time: '18:10', type: 'idling' },
    { time: '18:40', type: 'charging' },
  ]),
  thursday: buildTimeline([
    { time: '00:00', type: 'charging' },
    { time: '06:15', type: 'driving' },
    { time: '09:30', type: 'idling' },
    { time: '10:00', type: 'driving' },
    { time: '13:10', type: 'idling' },
    { time: '13:50', type: 'driving' },
    { time: '17:40', type: 'out_of_charge' },
  ]),
  friday: buildTimeline([
    { time: '00:00', type: 'charging' },
    { time: '05:45', type: 'driving' },
    { time: '09:00', type: 'idling' },
    { time: '09:25', type: 'driving' },
    { time: '12:20', type: 'idling' },
    { time: '12:50', type: 'driving' },
    { time: '15:30', type: 'charging' },
    { time: '16:05', type: 'driving' },
    { time: '18:30', type: 'charging' },
  ]),
  saturday: buildTimeline([
    { time: '00:00', type: 'charging' },
    { time: '07:30', type: 'idling' },
    { time: '08:10', type: 'driving' },
    { time: '11:15', type: 'idling' },
    { time: '12:00', type: 'driving' },
    { time: '14:10', type: 'charging' },
    { time: '14:55', type: 'idling' },
    { time: '15:30', type: 'driving' },
    { time: '17:10', type: 'idling' },
    { time: '17:45', type: 'charging' },
  ]),
  sunday: buildTimeline([
    { time: '00:00', type: 'charging' },
    { time: '09:30', type: 'idling' },
    { time: '10:15', type: 'driving' },
    { time: '11:20', type: 'idling' },
    { time: '13:00', type: 'driving' },
    { time: '14:10', type: 'idling' },
    { time: '18:00', type: 'charging' },
  ]),
};

export default vehicleOverviewTimelineByDay;
